import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { Button } from '@neo/index'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { ChevronRight, Copy, Eye, EyeOff } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useFlowStore } from 'v2/pages/flow/store'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { HandoffApiKey, HandoffConnection } from 'v2/types/api/handoff-context'
import { errorToast, successToast } from 'v2/utils/toasts'
import {
  DTMF_EXAMPLE,
  EXAMPLE_REQUEST,
  integrationInstructions,
  SIP_HEADER,
  uploadUrl,
} from './handoff-context.helpers'

const adminError = (title: string, action: string) => (error: unknown) =>
  errorToast({
    title,
    description:
      (error as AxiosError).response?.status === 403
        ? `Only admins can ${action}.`
        : getErrorText(error as AxiosError<ApiResponse<unknown>>),
  })

const copy = (text: string, what: string) =>
  navigator.clipboard.writeText(text).then(
    () => successToast({ title: `${what} copied` }),
    () => errorToast({ title: `${what} not copied`, description: 'Select and copy it manually.' }),
  )

interface ConnectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  connection: HandoffConnection
}

/** The organization's AI agent: the name human specialists see, the API key, the endpoint and the steps. */
export const ConnectionDialog = ({ open, onOpenChange, connection }: ConnectionDialogProps) => {
  const url = uploadUrl(resolveBaseUrl())
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-xl grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden"
        data-testid="qnr-context-connection"
      >
        <DialogHeader>
          <DialogTitle>Connect your AI agent</DialogTitle>
          <DialogDescription>Shared by every Flow in your organization.</DialogDescription>
        </DialogHeader>
        <NameField connection={connection} />
        {connection.is_admin ? (
          <KeyField />
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label>API key</Label>
            <span className="text-s-400 text-secondary">Only admins can view the API key.</span>
          </div>
        )}
        <Input
          label="Endpoint"
          value={url}
          readOnly
          inputClassName="font-jetbrains pr-10"
          endIcon={
            <ClipboardCopyButton textToCopy={url} aria-label="Copy endpoint" tooltipText="Copy" />
          }
          endIconClassName="pr-3"
        />
        <Steps url={url} />
        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => copy(integrationInstructions(url), 'Setup steps')}
          >
            Copy all steps
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const useRefreshConnection = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: conversationQueryKeys().handoffContextConnection })
}

const NameField = ({ connection }: { connection: HandoffConnection }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const refresh = useRefreshConnection()
  const [name, setName] = useState<string>()
  const draft = name ?? connection.name
  const rename = useMutation({
    mutationFn: async (value: string) =>
      (await handoffContextService.renameConnection(value)).data,
    onSuccess: () => {
      setName(undefined)
      return refresh()
    },
    onError: adminError('Name not saved', 'change the name'),
  })
  const changed = !!draft.trim() && draft.trim() !== connection.name

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Input
          label="Name shown to human specialists"
          value={draft}
          readOnly={!connection.is_admin}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {connection.is_admin && changed && (
        <Button
          type="button"
          variant="outline"
          disabled={!isEditing || rename.isPending}
          onClick={() => rename.mutate(draft.trim())}
        >
          Save
        </Button>
      )}
    </div>
  )
}

const MASK = '••••••••••••••••••••••••••••••••'

const KeyField = () => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const refresh = useRefreshConnection()
  // undefined until fetched; null when the stored key predates viewable keys
  const [apiKey, setApiKey] = useState<string | null>()
  const [visible, setVisible] = useState(false)
  const keyLoaded = (key: HandoffApiKey) => {
    setApiKey(key.api_key)
    return refresh()
  }
  const reveal = useMutation({
    mutationFn: async () => (await handoffContextService.revealKey()).data,
    onSuccess: keyLoaded,
    onError: adminError('API key not shown', 'view the API key'),
  })
  const regenerate = useMutation({
    mutationFn: async () => (await handoffContextService.regenerateKey()).data,
    onSuccess: (key) => {
      setVisible(true)
      successToast({ title: 'API key regenerated' })
      return keyLoaded(key)
    },
    onError: adminError('API key not regenerated', 'regenerate the API key'),
  })
  // The key leaves the server only when an admin asks for it.
  const withKey = (use: (key: string) => void) => {
    if (apiKey) return use(apiKey)
    if (apiKey === undefined)
      reveal.mutate(undefined, { onSuccess: (key) => key.api_key && use(key.api_key) })
  }
  const unavailable = reveal.isPending || apiKey === null

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="handoff-api-key">API key</Label>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            id="handoff-api-key"
            value={visible && apiKey ? apiKey : MASK}
            readOnly
            inputClassName="font-jetbrains pr-20"
            data-testid="qnr-context-api-key"
            endIconClassName="pr-1"
            endIcon={
              <div className="flex items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={unavailable}
                  aria-label={visible ? 'Hide API key' : 'Show API key'}
                  onClick={() => (visible ? setVisible(false) : withKey(() => setVisible(true)))}
                >
                  {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={unavailable}
                  aria-label="Copy API key"
                  onClick={() => withKey((key) => copy(key, 'API key'))}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
            }
          />
        </div>
        <ConfirmPopup
          title="Regenerate API key?"
          description="The current key stops working immediately. Update your AI agent with the new key."
          okButtonProps={{
            btnTxt: 'Regenerate',
            variant: 'destructive',
            onClick: () => regenerate.mutate(),
          }}
        >
          <Button type="button" variant="outline" disabled={!isEditing || regenerate.isPending}>
            Regenerate
          </Button>
        </ConfirmPopup>
      </div>
      {apiKey === null && (
        <span className="text-xs-400 text-secondary">
          This key was created before keys could be viewed again. Regenerate it to get a key you can
          copy.
        </span>
      )}
    </div>
  )
}

const Step = ({ mark, title, children }: { mark: string; title: string; children: ReactNode }) => (
  <li className="flex gap-3">
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-st2 font-jetbrains text-xxs-600 text-secondary">
      {mark}
    </span>
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-s-500 text-primary">{title}</span>
      <div className="text-s-400 text-secondary">{children}</div>
    </div>
  </li>
)

const Code = ({ children }: { children: ReactNode }) => (
  <code className="break-all rounded bg-bg2 px-1 font-jetbrains text-xs-400 text-primary">
    {children}
  </code>
)

const Disclosure = ({ label, children }: { label: string; children: ReactNode }) => (
  <Collapsible>
    <CollapsibleTrigger className="mt-1 flex items-center gap-1 text-left text-xs-500 text-brand-text [&[data-state=open]>svg]:rotate-90">
      <ChevronRight className="size-3.5 shrink-0 transition-transform" />
      {label}
    </CollapsibleTrigger>
    <CollapsibleContent>{children}</CollapsibleContent>
  </Collapsible>
)

const Steps = ({ url }: { url: string }) => (
  <div className="flex flex-col gap-3 border-t border-st2 pt-4">
    <span className="text-s-500 text-primary">How to connect your AI agent</span>
    <ol className="flex flex-col gap-4">
      <Step mark="1" title="Send the transfer context">
        POST it to the endpoint with <Code>Authorization: Bearer &lt;API key&gt;</Code>. Use a
        new handoff ID for every transfer: a UUID written as 32 lowercase hex characters.
        <Disclosure label="Example request">
          <div className="group relative mt-2">
            <pre className="overflow-x-auto rounded-md bg-bg2 p-3 font-jetbrains text-xs-400 text-primary">
              {EXAMPLE_REQUEST}
            </pre>
            <ClipboardCopyButton
              textToCopy={EXAMPLE_REQUEST}
              aria-label="Copy example request"
              tooltipText="Copy"
              position="topRight"
            />
          </div>
        </Disclosure>
      </Step>
      <Step mark="2" title="Transfer the call">
        Transfer it to this Flow&apos;s phone number with the SIP header <Code>{SIP_HEADER}</Code>{' '}
        set to the same handoff ID. The call never waits for the transfer context.
      </Step>
    </ol>
    <div className="flex flex-col gap-2 text-s-400 text-secondary">
      <Disclosure label="Optional: wait for a human specialist to answer">
        <p className="mt-1">
          For a warm transfer, keep your AI agent on the call and check{' '}
          <Code>GET {url}/&lt;handoff ID&gt;/status</Code> with the same header every 1–2 seconds.
          When it returns <Code>accepted</Code>, leave the call.
        </p>
      </Disclosure>
      <Disclosure label="Calls from outside Plivo?">
        <div className="mt-1 flex flex-col gap-2">
          <p>
            If your AI agent&apos;s calls don&apos;t run on Plivo, the transfer arrives as a regular
            phone call and the SIP header is lost. Send the handoff ID as keypad tones instead:
          </p>
          <ol className="flex list-decimal flex-col gap-1 pl-5">
            <li>
              In this Flow, add a Collect response step before Assign to human and turn on Receive
              AI transfer reference.
            </li>
            <li>
              Keep your AI agent on the call. As soon as it connects, play the handoff ID as 39
              keypad tones. The caller never types anything.
            </li>
          </ol>
          <Disclosure label="How to turn the handoff ID into 39 digits">
            <pre className="mt-2 overflow-x-auto rounded-md bg-bg2 p-3 font-jetbrains text-xs-400 text-primary">
              {DTMF_EXAMPLE}
            </pre>
          </Disclosure>
        </div>
      </Disclosure>
    </div>
  </div>
)
